import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Activity, Bot, MessageSquare, Package, X } from 'lucide-react';
import { useUserUsageAnalytics } from '../hooks/useUsers';
import { UserMediaUsageSection } from './UserMediaUsageSection';
import { FeatureUsageSection } from '../../feature-tracking';
import type { User, UserUsageChartPoint, UserUsageGranularity } from '../types';
import { formatAdminNumber, formatCompactAdminNumber } from '../../../../utils/numberFormat';

interface UserUsageModalProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
}

const GRANULARITY_OPTIONS: Array<{ value: UserUsageGranularity; label: string }> = [
  { value: 'day', label: 'Day' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

const SUMMARY_CARDS = [
  {
    key: 'chatSessionCount',
    label: 'Chat Sessions',
    Icon: MessageSquare,
    colorClass: 'text-emerald-500',
  },
  {
    key: 'agentCount',
    label: 'Agents',
    Icon: Bot,
    colorClass: 'text-violet-500',
  },
] as const;

const shouldShowLabel = (index: number, total: number) =>
  total <= 6 || index === 0 || index === total - 1 || index % Math.ceil(total / 4) === 0;

const UsageComboChart: React.FC<{ data: UserUsageChartPoint[] }> = ({ data }) => {
  const maxTokens = Math.max(
    1,
    ...data.flatMap((point) => [point.packageTokens, point.advanceTokens])
  );
  const maxCount = Math.max(1, ...data.flatMap((point) => [point.chatSessions, point.agents]));
  const totalPackageTokens = data.reduce((sum, point) => sum + point.packageTokens, 0);
  const totalAdvanceTokens = data.reduce((sum, point) => sum + point.advanceTokens, 0);
  const totalChatSessions = data.reduce((sum, point) => sum + point.chatSessions, 0);
  const totalAgents = data.reduce((sum, point) => sum + point.agents, 0);
  const chartLeft = 64;
  const chartRight = 58;
  const chartTop = 24;
  const chartHeight = 220;
  const chartBottom = 56;
  const groupWidth = 72;
  const width = Math.max(data.length * groupWidth + chartLeft + chartRight, 720);
  const height = chartTop + chartHeight + chartBottom;
  const chartWidth = width - chartLeft - chartRight;
  const baseline = chartTop + chartHeight;
  const pointStep = data.length > 1 ? chartWidth / (data.length - 1) : chartWidth;
  const groupSlot = chartWidth / Math.max(data.length, 1);
  const barWidth = Math.max(8, Math.min(22, groupSlot * 0.2));
  const barGap = 4;
  const getX = (index: number) =>
    data.length > 1 ? chartLeft + index * pointStep : chartLeft + chartWidth / 2;
  const getTokenY = (value: number) => baseline - (value / maxTokens) * chartHeight;
  const getPackageTokenLabelY = (value: number) => Math.max(chartTop + 12, getTokenY(value) - 10);
  const getAdvanceTokenLabelY = (value: number) => Math.min(baseline - 8, getTokenY(value) + 18);
  const packageTokenLinePoints = data
    .map((point, index) => `${getX(index)},${getTokenY(point.packageTokens)}`)
    .join(' ');
  const advanceTokenLinePoints = data
    .map((point, index) => `${getX(index)},${getTokenY(point.advanceTokens)}`)
    .join(' ');
  const legendItems = [
    {
      key: 'packageTokens' as const,
      label: 'Package Credits',
      value: totalPackageTokens,
      color: '#0ea5e9',
      markerClassName: 'h-0.5 w-5 rounded-full',
    },
    {
      key: 'advanceTokens' as const,
      label: 'Advance Credits',
      value: totalAdvanceTokens,
      color: '#f59e0b',
      markerClassName: 'h-0.5 w-5 rounded-full',
    },
    {
      key: 'chatSessions' as const,
      label: 'Chat Sessions',
      value: totalChatSessions,
      color: '#10b981',
      markerClassName: 'h-3 w-3 rounded-sm',
    },
    {
      key: 'agents' as const,
      label: 'Agents',
      value: totalAgents,
      color: '#8b5cf6',
      markerClassName: 'h-3 w-3 rounded-sm',
    },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-4 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Usage Trends</h3>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {legendItems.map((item) => (
            <div
              key={item.key}
              className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400"
            >
              <span
                className={item.markerClassName}
                style={{ backgroundColor: item.color }}
                aria-hidden="true"
              />
              <span>
                {item.label}: {formatAdminNumber(item.value)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {data.length ? (
        <div className="overflow-x-auto pb-1">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            style={{ width: '100%', minWidth: `${width}px`, height: `${height}px` }}
            role="img"
            aria-label="User package credits, advance credits, chat sessions, and agents usage chart"
          >
            <text
              x={chartLeft}
              y={12}
              textAnchor="start"
              className="fill-gray-400 text-[11px] font-medium dark:fill-slate-500"
            >
              Tokens
            </text>
            <text
              x={width - chartRight}
              y={12}
              textAnchor="end"
              className="fill-gray-400 text-[11px] font-medium dark:fill-slate-500"
            >
              Count
            </text>
            {[0, 0.5, 1].map((ratio) => {
              const y = baseline - ratio * chartHeight;

              return (
                <g key={ratio}>
                  <line
                    x1={chartLeft}
                    y1={y}
                    x2={width - chartRight}
                    y2={y}
                    stroke="currentColor"
                    className="text-gray-200 dark:text-slate-700"
                    strokeWidth="1"
                  />
                  <text
                    x={chartLeft - 10}
                    y={y + 4}
                    textAnchor="end"
                    className="fill-gray-400 text-[11px] dark:fill-slate-500"
                  >
                    {formatAdminNumber(Math.round(maxTokens * ratio))}
                  </text>
                  <text
                    x={width - chartRight + 10}
                    y={y + 4}
                    textAnchor="start"
                    className="fill-gray-400 text-[11px] dark:fill-slate-500"
                  >
                    {formatAdminNumber(Math.round(maxCount * ratio))}
                  </text>
                </g>
              );
            })}

            <line
              x1={chartLeft}
              y1={baseline}
              x2={width - chartRight}
              y2={baseline}
              stroke="currentColor"
              className="text-gray-300 dark:text-slate-600"
              strokeWidth="1"
            />

            {data.map((point, index) => {
              const x = getX(index);
              const sessionHeight = (point.chatSessions / maxCount) * chartHeight;
              const agentHeight = (point.agents / maxCount) * chartHeight;
              const sessionX = x - barWidth - barGap / 2;
              const agentX = x + barGap / 2;

              return (
                <g
                  key={point.key}
                  className="transition-opacity hover:opacity-80"
                  aria-label={`${point.label}: ${formatAdminNumber(
                    point.packageTokens
                  )} package tokens, ${formatAdminNumber(
                    point.advanceTokens
                  )} advance tokens, ${formatAdminNumber(
                    point.chatSessions
                  )} chat sessions, ${formatAdminNumber(point.agents)} agents`}
                >
                  <title>
                    {`${point.label}: ${formatAdminNumber(
                      point.packageTokens
                    )} package tokens, ${formatAdminNumber(
                      point.advanceTokens
                    )} advance tokens, ${formatAdminNumber(
                      point.chatSessions
                    )} chat sessions, ${formatAdminNumber(point.agents)} agents`}
                  </title>
                  <rect
                    x={sessionX}
                    y={baseline - sessionHeight}
                    width={barWidth}
                    height={Math.max(point.chatSessions > 0 ? 2 : 0, sessionHeight)}
                    rx="3"
                    fill="#10b981"
                    opacity={point.chatSessions > 0 ? 0.88 : 0.18}
                  />
                  <rect
                    x={agentX}
                    y={baseline - agentHeight}
                    width={barWidth}
                    height={Math.max(point.agents > 0 ? 2 : 0, agentHeight)}
                    rx="3"
                    fill="#8b5cf6"
                    opacity={point.agents > 0 ? 0.88 : 0.18}
                  />
                  {shouldShowLabel(index, data.length) && (
                    <text
                      x={x}
                      y={baseline + 24}
                      textAnchor="middle"
                      className="fill-gray-500 text-[11px] dark:fill-slate-400"
                    >
                      {point.label}
                    </text>
                  )}
                </g>
              );
            })}

            {data.length > 1 && (
              <>
                <polyline
                  points={packageTokenLinePoints}
                  fill="none"
                  stroke="#0ea5e9"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <polyline
                  points={advanceTokenLinePoints}
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            )}
            {data.map((point, index) => {
              const x = getX(index);
              const packageY = getTokenY(point.packageTokens);
              const advanceY = getTokenY(point.advanceTokens);

              return (
                <g key={`${point.key}-token-points`}>
                  <circle
                    cx={x}
                    cy={packageY}
                    r="4"
                    fill="#0ea5e9"
                    stroke="#ffffff"
                    strokeWidth="2"
                  >
                    <title>{`${point.label}: ${formatAdminNumber(
                      point.packageTokens
                    )} package credits`}</title>
                  </circle>
                  <text
                    x={x}
                    y={getPackageTokenLabelY(point.packageTokens)}
                    textAnchor="middle"
                    className="fill-sky-600 text-[10px] font-semibold dark:fill-sky-300"
                  >
                    {formatCompactAdminNumber(point.packageTokens)}
                  </text>
                  <circle
                    cx={x}
                    cy={advanceY}
                    r="4"
                    fill="#f59e0b"
                    stroke="#ffffff"
                    strokeWidth="2"
                  >
                    <title>{`${point.label}: ${formatAdminNumber(
                      point.advanceTokens
                    )} advance credits`}</title>
                  </circle>
                  <text
                    x={x}
                    y={getAdvanceTokenLabelY(point.advanceTokens)}
                    textAnchor="middle"
                    className="fill-amber-600 text-[10px] font-semibold dark:fill-amber-300"
                  >
                    {formatCompactAdminNumber(point.advanceTokens)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      ) : (
        <div className="flex h-72 items-center justify-center rounded-lg border border-dashed border-gray-200 text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
          No chart data
        </div>
      )}
    </div>
  );
};

export const UserUsageModal: React.FC<UserUsageModalProps> = ({ open, user, onClose }) => {
  const [granularity, setGranularity] = useState<UserUsageGranularity>('day');
  const { data, isLoading, isFetching, isError, error } = useUserUsageAnalytics(
    open ? user?.publicId : undefined
  );

  useEffect(() => {
    if (open) {
      setGranularity('day');
    }
  }, [open, user?.publicId]);

  const chartData = useMemo(() => data?.charts[granularity] ?? [], [data?.charts, granularity]);

  const displayName = user?.userName || user?.email || 'User';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(15, 23, 42)' : 'white'),
          color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <div className="flex min-w-0 items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold text-gray-900 dark:text-white">
              {displayName}
            </p>
            <p className="truncate text-sm font-normal text-gray-500 dark:text-slate-400">
              Usage analytics
            </p>
          </div>
          <IconButton
            aria-label="Close usage analytics"
            onClick={onClose}
            sx={{
              color: (theme) =>
                theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)',
            }}
          >
            <X size={20} />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {isLoading ? (
          <div className="flex min-h-80 items-center justify-center">
            <CircularProgress sx={{ color: '#3B82F6' }} />
          </div>
        ) : isError ? (
          <Alert severity="error">{error?.message || 'Unable to load user usage analytics.'}</Alert>
        ) : data ? (
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-slate-400">
                  <Activity size={17} className="text-sky-500" />
                  Tokens Used
                </div>
                <div className="mt-3 space-y-3">
                  <div>
                    <div className="flex items-center gap-2 text-xs font-medium uppercase text-gray-500 dark:text-slate-400">
                      <Package size={14} className="text-sky-500" />
                      Package
                    </div>
                    <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                      {formatAdminNumber(data.summary.packageTokensUsed)}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-xs font-medium uppercase text-gray-500 dark:text-slate-400">
                      <Activity size={14} className="text-amber-500" />
                      Advance
                    </div>
                    <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                      {formatAdminNumber(data.summary.advanceTokensUsed)}
                    </div>
                  </div>
                </div>
              </div>
              {SUMMARY_CARDS.map(({ key, label, Icon, colorClass }) => (
                <div
                  key={key}
                  className="rounded-lg border border-gray-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-slate-400">
                    <Icon size={17} className={colorClass} />
                    {label}
                  </div>
                  <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                    {formatAdminNumber(data.summary[key])}
                  </div>
                </div>
              ))}
            </div>

            <UserMediaUsageSection publicId={user?.publicId} />

            <FeatureUsageSection userPublicId={user?.publicId} bare />

            <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={granularity}
                onChange={(_, value: UserUsageGranularity | null) => {
                  if (value) {
                    setGranularity(value);
                  }
                }}
                aria-label="Usage chart granularity"
              >
                {GRANULARITY_OPTIONS.map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {isFetching && (
                <span className="text-sm text-gray-500 dark:text-slate-400">Refreshing...</span>
              )}
            </div>

            <UsageComboChart data={chartData} />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
